/**
 * EntityDetailShell — config-driven detail page header + lazy tab bar.
 *
 * Features:
 * - Header: profile picture (ImageUpload when editable), title, status chip,
 *   subtitle, Edit button.
 * - Tab bar driven by config.tabs.
 * - Lazy loading: each tab's content is only mounted after first activation
 *   (lazyLoad default true) — a failing tab does NOT blank the page.
 * - Per-tab error boundary (inline error + retry, not full-page).
 * - Edit pre-load: always fetches fresh entity by ID before opening modal.
 */

import React, { Suspense, useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/common/Button';
import { StatusBadge } from '@/components/common/Badge';
import { Tabs, TabPanel } from '@/components/common/Tabs';
import { Card } from '@/components/common/Card';
import { Avatar } from '@/components/common/Avatar';
import { ActionMenu } from '@/components/common/ActionMenu';
import { ImageUpload } from './ImageUpload';
import type { EntityDetailConfig } from './types';

// ── Styles ────────────────────────────────────────────────────────────────

const BackBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 14px;
  margin-bottom: 16px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  &:hover {
    color: ${({ theme }) => theme.colors.text};
  }
`;

const HeaderCard = styled(Card)`
  display: flex;
  gap: 20px;
  align-items: flex-start;
  margin-bottom: 20px;
  flex-wrap: wrap;
`;

const HeaderMeta = styled.div`
  flex: 1;
  min-width: 0;
`;

/** Two-column body when a sidebar is configured; unchanged single column
 *  otherwise, so entities without a rail render exactly as before. */
const BodyLayout = styled.div<{ $hasSidebar: boolean }>`
  display: grid;
  gap: 20px;
  align-items: start;
  grid-template-columns: ${({ $hasSidebar }) => ($hasSidebar ? '320px minmax(0, 1fr)' : '1fr')};

  @media (max-width: 1024px) {
    /* Identity before detail on narrow screens — the rail stacks above. */
    grid-template-columns: 1fr;
  }
`;

const SidebarColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
`;

const EntityTitle = styled.h2`
  font-size: 22px;
  font-weight: 700;
  margin: 0 0 8px;
  color: ${({ theme }) => theme.colors.text};
`;

const SubtitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 14px;
`;

const TabErrorBox = styled.div`
  padding: 24px;
  text-align: center;
  color: ${({ theme }) => theme.colors.error};
  font-size: 14px;
`;

const TabLoadingBox = styled.div`
  padding: 24px;
  color: ${({ theme }) => theme.colors.textTertiary};
  font-size: 14px;
`;

// ── Per-tab error boundary ───────────────────────────────────────────────

interface TabErrorBoundaryState {
  hasError: boolean;
  message: string;
}

class TabErrorBoundary extends React.Component<
  { children: React.ReactNode; tabId: string },
  TabErrorBoundaryState
> {
  constructor(props: { children: React.ReactNode; tabId: string }) {
    super(props);
    this.state = { hasError: false, message: '' };
  }

  static getDerivedStateFromError(error: unknown): TabErrorBoundaryState {
    const message =
      error instanceof Error ? error.message : 'An unexpected error occurred.';
    return { hasError: true, message };
  }

  retry = () => this.setState({ hasError: false, message: '' });

  render() {
    if (this.state.hasError) {
      return (
        <TabErrorBox>
          <p>{this.state.message}</p>
          <button onClick={this.retry} style={{ marginTop: 8 }}>
            Retry
          </button>
        </TabErrorBox>
      );
    }
    return this.props.children;
  }
}

// ── Lazy tab content ─────────────────────────────────────────────────────

interface LazyTabProps {
  tabId: string;
  activeTab: string;
  lazyLoad: boolean;
  children: React.ReactNode;
}

const LazyTab: React.FC<LazyTabProps> = ({ tabId, activeTab, lazyLoad, children }) => {
  const [everActive, setEverActive] = useState(tabId === activeTab);

  if (!everActive && activeTab === tabId) {
    setEverActive(true);
  }

  if (!everActive && lazyLoad) return null;

  return (
    <div hidden={activeTab !== tabId}>
      <TabErrorBoundary tabId={tabId}>
        <Suspense fallback={<TabLoadingBox>Loading…</TabLoadingBox>}>
          {children}
        </Suspense>
      </TabErrorBoundary>
    </div>
  );
};

// ── EntityDetailShell ────────────────────────────────────────────────────

interface EntityDetailShellProps<T> {
  entity: T;
  config: EntityDetailConfig<T>;
  /** Tenant ID for image uploads. */
  tenantId?: string;
  /** Called when the Edit button is clicked. Parent handles modal open. */
  onEdit: () => void;
  /** Called with new photo URL after upload; parent persists via PATCH. */
  onPhotoUpload?: (url: string) => void;
  canEdit?: boolean;
}

export function EntityDetailShell<T extends { id: string }>({
  entity,
  config,
  tenantId,
  onEdit,
  onPhotoUpload,
  canEdit = true,
}: EntityDetailShellProps<T>) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(config.tabs[0]?.id ?? '');

  const title = config.getTitle(entity);
  const subtitle = config.getSubtitle?.(entity);
  const statusVal = config.getStatus?.(entity);
  const photoUrl = config.getPhotoUrl?.(entity);

  const tabBarItems = config.tabs.map((t) => ({ id: t.id, label: t.label }));

  return (
    <div
      data-testid={`${config.testIdPrefix}-detail-page`}
      data-flow={`${config.entityName}-detail-page`}
    >
      <BackBtn onClick={() => navigate(config.routeBase)}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="15 18 9 12 15 6" />
        </svg>
        Back to {config.entityName.charAt(0).toUpperCase() + config.entityName.slice(1)}s
      </BackBtn>

      {/* ── Header card ──────────────────────────────────────────── */}
      <HeaderCard padding="lg">
        {/* Profile picture */}
        {canEdit && onPhotoUpload && tenantId ? (
          <ImageUpload
            currentUrl={photoUrl}
            onUpload={onPhotoUpload}
            storagePath={entity.id}
            tenantId={tenantId}
            entityType={config.entityType ?? config.entityName + 's'}
          />
        ) : (
          <Avatar
            firstName={title.split(' ')[0]}
            lastName={title.split(' ')[1] ?? ''}
            src={photoUrl ?? undefined}
            size="xl"
          />
        )}

        <HeaderMeta>
          <EntityTitle data-testid={`${config.testIdPrefix}-detail-title`}>
            {title}
          </EntityTitle>

          <SubtitleRow>
            {subtitle && <span>{subtitle}</span>}
            {statusVal && (
              <StatusBadge status={statusVal} />
            )}
          </SubtitleRow>

          {/* `canEdit` gates BOTH branches. Previously an `actions` config
              bypassed it entirely, so the first adopter would have silently
              lost the permission check that the bare Edit button had. */}
          {canEdit && config.actions?.length ? (
            <ActionMenu
              items={config.actions}
              triggerLabel={`Actions for this ${config.entityName}`}
              testId={`${config.testIdPrefix}-actionmenu-detail`}
            />
          ) : (
            canEdit && (
              <Button
                size="sm"
                variant="outline"
                onClick={onEdit}
                data-testid={`${config.testIdPrefix}-detail-edit-btn`}
              >
                Edit
              </Button>
            )
          )}
        </HeaderMeta>
      </HeaderCard>

      {/* ── Sidebar + tabs ──────────────────────────────────────── */}
      <BodyLayout $hasSidebar={Boolean(config.renderSidebar)}>
        {config.renderSidebar && (
          <SidebarColumn data-testid={`${config.testIdPrefix}-detail-sidebar`}>
            {config.renderSidebar(entity)}
          </SidebarColumn>
        )}
        <Card padding="lg">
          <Tabs tabs={tabBarItems} activeTab={activeTab} onChange={setActiveTab}>
            {config.tabs.map((tab) => (
              <LazyTab
                key={tab.id}
                tabId={tab.id}
                activeTab={activeTab}
                lazyLoad={tab.lazyLoad !== false}
              >
                <TabPanel value={tab.id} activeValue={activeTab}>
                  {tab.render(entity.id)}
                </TabPanel>
              </LazyTab>
            ))}
          </Tabs>
        </Card>
      </BodyLayout>
    </div>
  );
}




